import { CareProfileResponse } from 'data-types';

export type TourStepPlacement = 'above' | 'below' | 'left' | 'center';

export interface TourStepConfig {
	key: string;
	anchorId: string | null;
	/**
	 * Optional extra anchor ids whose bounding boxes are unioned with `anchorId` to form a
	 * wider spotlight on desktop only (>=1024px). Missing ids are ignored.
	 */
	desktopAnchorIds?: string[];
	/**
	 * Optional override for the primary anchor id on mobile (<1024px), when the same component
	 * is rendered twice (a mobile layout copy and a desktop layout copy) with different ids.
	 */
	mobileAnchorId?: string;
	titleKey: string;
	bodyKey: string;
	placement: TourStepPlacement;
	requires?: (careProfile: CareProfileResponse) => boolean;
}

export const TOUR_STEPS: TourStepConfig[] = [
	{
		key: 'progress',
		anchorId: 'weight-trend-chart',
		desktopAnchorIds: ['weight-progress-card'],
		titleKey: 'CareProfile/Tour/Step1/Title',
		bodyKey: 'CareProfile/Tour/Step1/Body',
		placement: 'below',
		requires: (careProfile) => Boolean(careProfile.weight),
	},
	{
		key: 'careTeam',
		anchorId: 'care-team-cards',
		mobileAnchorId: 'care-team-cards-mobile',
		titleKey: 'CareProfile/Tour/Step2/Title',
		bodyKey: 'CareProfile/Tour/Step2/Body',
		// Desktop: tooltip sits to the left of the card. Mobile: falls back to sitting above it (see
		// CareProfileTour's handling of 'left' on narrow viewports).
		placement: 'left',
		requires: (careProfile) => careProfile.careTeam.length > 0,
	},
	{
		key: 'timeline',
		anchorId: 'treatment-cycle-timeline',
		titleKey: 'CareProfile/Tour/Step3/Title',
		bodyKey: 'CareProfile/Tour/Step3/Body',
		placement: 'above',
		requires: (careProfile) => careProfile.timeline.upcoming.length > 0 || careProfile.timeline.past.length > 0,
	},
	{
		key: 'quickLinks',
		anchorId: 'care-profile-quick-links',
		desktopAnchorIds: ['care-profile-support-chat-button'],
		titleKey: 'CareProfile/Tour/Step4/Title',
		bodyKey: 'CareProfile/Tour/Step4/Body',
		placement: 'above',
	},
	{
		key: 'final',
		anchorId: null,
		titleKey: 'CareProfile/Tour/Final/Title',
		bodyKey: 'CareProfile/Tour/Final/Body',
		placement: 'center',
	},
];

const SEEN_STORAGE_PREFIX = 'careplus_tour_seen_';

const seenKey = (customerId: string): string => `${SEEN_STORAGE_PREFIX}${customerId}`;

export const isTourSeen = (customerId: string): boolean => {
	try {
		return window.localStorage.getItem(seenKey(customerId)) === '1';
	} catch {
		return true; // if localStorage is unavailable, behave as if seen so we never get stuck
	}
};

export const markTourSeen = (customerId: string): void => {
	try {
		window.localStorage.setItem(seenKey(customerId), '1');
	} catch {
		/* no-op */
	}
};
